import { getSupabase, isSupabaseConfigured, bootstrapSupabaseConfig } from './client';
import { LEARNER_TABLES } from './tables';
import { LEARNING_CONSENT_VERSION } from '../../constants/jyotishaLearningConsent';

/**
 * @typedef {{ consentVersion: string; acceptedAt: string } | null} LearningConsentRecord
 */

/**
 * @param {string} userId
 * @returns {Promise<LearningConsentRecord>}
 */
export async function fetchLearningConsent(userId) {
  if (!userId) return null;
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) return null;

  const client = getSupabase();
  if (!client) return null;

  const { data, error } = await client
    .from(LEARNER_TABLES.userLearningConsent)
    .select('consent_version, accepted_at')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[learningConsent] fetch failed:', error.message);
    throw error;
  }

  if (!data) return null;

  return {
    consentVersion: data.consent_version,
    acceptedAt: data.accepted_at,
  };
}

/**
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function hasCurrentLearningConsent(userId) {
  const row = await fetchLearningConsent(userId);
  return row?.consentVersion === LEARNING_CONSENT_VERSION;
}

/**
 * Record user acceptance of the learning consent terms.
 * @param {string} userId
 * @returns {Promise<LearningConsentRecord>}
 */
export async function recordLearningConsent(userId) {
  if (!userId) throw new Error('Sign in required to record consent.');
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  if (!isSupabaseConfigured()) {
    throw new Error('Consent storage is not configured yet.');
  }

  const client = getSupabase();
  if (!client) throw new Error('Could not connect to consent storage.');

  const payload = {
    user_id: userId,
    consent_version: LEARNING_CONSENT_VERSION,
    accepted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 512) : null,
  };

  const { data, error } = await client
    .from(LEARNER_TABLES.userLearningConsent)
    .upsert(payload, { onConflict: 'user_id' })
    .select('consent_version, accepted_at')
    .single();

  if (error) throw error;

  return {
    consentVersion: data.consent_version,
    acceptedAt: data.accepted_at,
  };
}
